import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Handshake, Calendar, FileText, ShieldCheck, ArrowRight, Info } from 'lucide-react';
import api from '../../services/api';

const MinhaParceria = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/parcerias/minha-parceria')
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-12 text-muted-foreground">A carregar...</div>;
  if (!data) return <div className="text-center py-12 text-muted-foreground">Parceria nao encontrada</div>;

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-PT') : 'Sem data';

  return (
    <div className="space-y-6 p-4" data-testid="minha-parceria">
      {/* Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <Handshake className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold">{data.nome}</h2>
                <p className="text-sm text-muted-foreground">{data.tipo_entidade}</p>
              </div>
            </div>
            <Badge variant={data.estado === 'Activa' ? 'default' : 'destructive'} className="text-sm px-3 py-1">
              {data.estado}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Objetivo */}
      {data.objetivo && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Info className="h-4 w-4" /> Objetivo da Parceria
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm">{data.objetivo}</p>
          </CardContent>
        </Card>
      )}

      {/* Detalhes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Periodo</span>
            </div>
            <p className="text-sm">{fmtDate(data.data_inicio)} - {data.data_fim ? fmtDate(data.data_fim) : 'Sem fim'}</p>
            <p className="text-xs text-muted-foreground mt-1">Modelo: {data.modelo}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Vales</span>
            </div>
            <p className="text-sm">{data.total_vales_validos} validos / {data.total_vales_utilizados} utilizados</p>
            <p className="text-xs text-muted-foreground mt-1">Validade: {data.dias_validade_vale} dias</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Regras</span>
            </div>
            <p className="text-sm">{data.regra_acumulacao || '-'}</p>
            <p className="text-xs text-muted-foreground mt-1">{data.bloqueia_se_vale_ativo ? 'Bloqueia se vale activo' : 'Permite varios vales'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Regras Janeira -> Parceiro */}
      {data.regras_janeira_parceiro?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-blue-500" />
              Janeira <ArrowRight className="h-3 w-3" /> {data.nome}
              <span className="text-xs text-muted-foreground font-normal ml-2">(Cliente compra na Janeira, usa beneficio no parceiro)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.regras_janeira_parceiro.map((r, i) => (
                <div key={i} className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-sm">
                        {r.tipo_beneficio === '€/Litro' && `${r.valor_beneficio} ${r.tipo_beneficio} (max ${r.limite_litros || '?'}L)`}
                        {r.tipo_beneficio === '%' && `${r.valor_beneficio}% de desconto`}
                        {r.tipo_beneficio === '€' && `${r.valor_beneficio} EUR de desconto`}
                        {!['€/Litro', '%', '€'].includes(r.tipo_beneficio) && `${r.valor_beneficio} ${r.tipo_beneficio}`}
                      </span>
                      {r.valor_minimo_compra > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">Compra minima: {r.valor_minimo_compra} EUR</p>
                      )}
                    </div>
                    <Badge variant="outline" className="text-xs">{r.quem_suporta || 'Janeira'} suporta</Badge>
                  </div>
                  {(r.data_inicio || r.data_fim) && (
                    <p className="text-xs text-muted-foreground mt-2">Periodo: {fmtDate(r.data_inicio)} - {r.data_fim ? fmtDate(r.data_fim) : 'Sem fim'}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Regras Parceiro -> Janeira */}
      {data.ativar_parceiro_janeira && data.regras_parceiro_janeira?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRight className="h-4 w-4 text-green-500" />
              {data.nome} <ArrowRight className="h-3 w-3" /> Janeira
              <span className="text-xs text-muted-foreground font-normal ml-2">(Cliente compra no parceiro, usa beneficio na Janeira)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.regras_parceiro_janeira.map((r, i) => (
                <div key={i} className="p-3 bg-green-50 border border-green-100 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">
                      {r.valor_beneficio} {r.tipo_beneficio}
                      {r.valor_minimo_compra > 0 && ` (min. ${r.valor_minimo_compra} EUR)`}
                    </span>
                    <Badge variant="outline" className="text-xs">{r.quem_suporta || 'Parceiro'} suporta</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sem regras */}
      {data.regras_janeira_parceiro?.length === 0 && (!data.ativar_parceiro_janeira || data.regras_parceiro_janeira?.length === 0) && (
        <Card>
          <CardContent className="pt-6 text-center text-muted-foreground">
            <p>Nenhuma regra de desconto configurada para esta parceria.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MinhaParceria;
